import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";

export interface GymArticle {
  id: string;
  gym_id: string;
  name: string;
  description: string | null;
  price: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateArticleData {
  gym_id: string;
  name: string;
  description?: string | null;
  price: number;
}

export interface UpdateArticleData {
  name?: string;
  description?: string | null;
  price?: number;
  is_active?: boolean;
}

export const useGymArticles = (gymId: string | null) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();

  const { data: articles, isLoading } = useQuery({
    queryKey: ["gym-articles", gymId],
    queryFn: async () => {
      if (!gymId) return [];
      const { data, error } = await supabase
        .from("gym_articles")
        .select("*")
        .eq("gym_id", gymId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as GymArticle[];
    },
    enabled: !!gymId,
  });

  const createArticle = useMutation({
    mutationFn: async (data: CreateArticleData) => {
      const { data: result, error } = await supabase
        .from("gym_articles")
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gym-articles", gymId] });
      success("Artículo creado");
    },
    onError: (e) => {
      console.error(e);
      showError("No se pudo crear el artículo");
    },
  });

  const updateArticle = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateArticleData }) => {
      const { data: result, error } = await supabase
        .from("gym_articles")
        .update(data)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gym-articles", gymId] });
      success("Artículo actualizado");
    },
    onError: (e) => {
      console.error(e);
      showError("No se pudo actualizar el artículo");
    },
  });

  const deleteArticle = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("gym_articles").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gym-articles", gymId] });
      success("Artículo eliminado");
    },
    onError: (e) => {
      console.error(e);
      showError("No se pudo eliminar el artículo");
    },
  });

  return {
    articles: articles || [],
    isLoading,
    createArticle,
    updateArticle,
    deleteArticle,
  };
};
